import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitTextReveal from './SplitTextReveal';
import FadeInOnScroll from './FadeInOnScroll';

gsap.registerPlugin(ScrollTrigger);

/**
 * OffersCarousel Component
 * Custom carousel with 3D stacked photo transition effect
 * 
 * Features:
 * - Each offer has one image
 * - All images form a single stack (visible across all slides)
 * - Top image corresponds to current offer (0deg rotation)
 * - Next 2 images have rotations (4deg, 8deg)
 * - 3D transition shuffles stack when navigating
 * - Uses proper scroll-into-viewport animation system for slide transitions
 */
export default class OffersCarousel {
  constructor(element) {
    this.element = element;
    this.section = element.closest('.offers-feature');
    this.track = element.querySelector('.offers-carousel-track');
    this.items = Array.from(element.querySelectorAll('.offer-item'));
    this.imagesStack = element.querySelector('.offers-images-stack');
    this.images = Array.from(element.querySelectorAll('.offer-image'));
    this.prevBtn = element.querySelector('.carousel-prev');
    this.nextBtn = element.querySelector('.carousel-next');
    this.currentCounter = element.querySelector('.carousel-counter .current');
    this.totalCounter = element.querySelector('.carousel-counter .total');
    
    this.currentIndex = 0;
    this.isAnimating = false;
    
    // Store original content for each animated element (for clean resets)
    this.originalContent = new Map();
    this.items.forEach(item => {
      const animatedElements = item.querySelectorAll('[data-split-text-reveal], [data-fade-in]');
      animatedElements.forEach(el => {
        // For split text elements, we need to preserve special markup like .last-word
        if (el.hasAttribute('data-split-text-reveal')) {
          // Clone the element to get clean HTML before any animation processing
          const clone = el.cloneNode(true);
          this.originalContent.set(el, {
            html: clone.innerHTML
          });
        } else {
          // For fade elements, just store the HTML
          this.originalContent.set(el, {
            html: el.innerHTML
          });
        }
      });
    });
    
    if (this.items.length === 0) {
      return;
    }
    
    this.init();
  }
  
  init() {
    // Set total counter
    if (this.totalCounter) {
      this.totalCounter.textContent = this.items.length;
    }
    
    // Initialize the image stack
    this.updateImageStack(false); // No animation on initial load
    
    // Bind navigation events
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.previous());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
    
    // Update initial state
    this.updateCounter();
    this.enableButtons();
    
    // Set up initial scroll-triggered animation for the first slide
    this.setupInitialAnimation();
  }
  
  /**
   * Set up scroll-triggered animation for initial slide (only runs once)
   */
  setupInitialAnimation() {
    const firstSlide = this.items[this.currentIndex];
    if (!firstSlide) return;
    
    // Find elements that need animation
    const splitTextElements = firstSlide.querySelectorAll('[data-split-text-reveal]');
    const fadeElements = firstSlide.querySelectorAll('[data-fade-in]');
    
    // Create ScrollTriggers for the initial slide
    splitTextElements.forEach((element) => {
      const instance = new SplitTextReveal(element);
    });
    
    fadeElements.forEach((element) => {
      const instance = new FadeInOnScroll(element);
    });
  }
  
  /**
   * Update the image stack based on the current index. The original carousel
   * motion uses CSS transforms so the three visible cards return together.
   */
  updateImageStack(animate = true) {
    const visibleCount = Math.min(3, this.images.length);
    const rotations = [
      { z: 0, x: 0, y: 0 },
      { z: 5, x: -8, y: -8 },
      { z: -3, x: -12, y: -16 }
    ];
    const zIndexes = [3, 2, 1];
    const updates = [];

    this.images.forEach((image, imgIndex) => {
      let stackPosition = imgIndex - this.currentIndex;
      if (stackPosition < 0) {
        stackPosition += this.images.length;
      }

      const isVisible = stackPosition < visibleCount;
      if (isVisible) {
        const rotation = rotations[stackPosition];
        updates.push({
          image,
          transform: `translate(-50%, -50%) translateY(${rotation.y}px) rotateX(${rotation.x}deg) rotateZ(${rotation.z}deg)`,
          zIndex: zIndexes[stackPosition].toString(),
          display: 'block'
        });
      } else {
        updates.push({ image, display: 'none' });
      }
    });

    if (animate) {
      updates.forEach(update => {
        if (update.display === 'block') {
          update.image.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        }
      });
      void this.imagesStack.offsetHeight;
    } else {
      updates.forEach(update => {
        update.image.style.transition = 'none';
      });
    }

    updates.forEach(update => {
      if (update.display === 'block') {
        update.image.style.transform = update.transform;
        update.image.style.zIndex = update.zIndex;
        update.image.style.display = 'block';
      } else {
        update.image.style.display = 'none';
      }
    });
  }
  
  /**
   * Navigate to next item (with looping)
   */
  next() {
    if (this.isAnimating) {
      return;
    }
    
    const nextIndex = (this.currentIndex + 1) % this.items.length;
    this.goToSlide(nextIndex, 'next');
  }
  
  /**
   * Navigate to previous item (with looping)
   */
  previous() {
    if (this.isAnimating) {
      return;
    }
    
    const prevIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
    this.goToSlide(prevIndex, 'prev');
  }
  
  /**
   * Go to specific slide with 3D transition
   */
  goToSlide(newIndex, direction) {
    if (this.isAnimating || newIndex === this.currentIndex) {
      return;
    }
    
    this.isAnimating = true;
    this.section?.classList.add('is-carousel-transitioning');
    
    const currentItem = this.items[this.currentIndex];
    const newItem = this.items[newIndex];
    const previousIndex = this.currentIndex;
    
    // Update counter immediately so it changes right away
    const oldIndex = this.currentIndex;
    this.currentIndex = newIndex;
    this.updateCounter();
    this.currentIndex = oldIndex; // Restore temporarily, will be set properly later
    
    // Disable buttons during animation
    this.disableButtons();
    
    // Dispatch slideOutStart event
    this.dispatchEvent('slideOutStart', {
      previousIndex,
      currentIndex: newIndex,
      direction,
      currentSlide: currentItem,
      nextSlide: newItem
    });
    
    // Prepare next slide (make it active with higher z-index but opacity 0)
    newItem.classList.add('active');
    newItem.style.zIndex = '10';

    // Keep the outgoing slide rendered for its existing fade-out window. Removing
    // .active here made CSS apply display:none before GSAP could animate opacity.
    currentItem.classList.add('is-exiting');

    const newAnimatedElements = newItem.querySelectorAll('[data-split-text-reveal], [data-fade-in]');
    gsap.set(newAnimatedElements, { opacity: 0 });
    
    // Fade out current slide content
    const currentAnimatedElements = currentItem.querySelectorAll('[data-split-text-reveal], [data-fade-in]');
    gsap.to(currentAnimatedElements, {
      opacity: 0,
      duration: 0.2,
      ease: 'sine.inOut',
      onComplete: () => {
        // The fade duration is unchanged; only remove the previous slide once
        // it has reached opacity 0, then continue the existing stack shuffle.
        currentItem.classList.remove('active', 'is-exiting');
        currentItem.style.zIndex = '';

        if (direction === 'next') {
          this.animateStackShuffle(newIndex, () => {
            this.handleSlideTransition(currentItem, newItem, previousIndex, newIndex, direction);
          });
        } else {
          this.animateStackShuffleReverse(newIndex, () => {
            this.handleSlideTransition(currentItem, newItem, previousIndex, newIndex, direction);
          });
        }
      }
    });
  }
  
  /**
   * Handle slide transition (shared logic for next/prev)
   */
  handleSlideTransition(currentItem, newItem, previousIndex, newIndex, direction) {
    // Clean up previous slide animations
    this.cleanupSlideAnimations(currentItem);
    
    // Dispatch slideOutComplete event
    this.dispatchEvent('slideOutComplete', {
      previousIndex,
      currentIndex: newIndex,
      direction,
      previousSlide: currentItem
    });
    
    // Reset z-index on new slide
    newItem.style.zIndex = '';
    
    // Update index
    this.currentIndex = newIndex;
    
    // Dispatch slideInStart event
    this.dispatchEvent('slideInStart', {
      previousIndex,
      currentIndex: newIndex,
      direction,
      currentSlide: newItem
    });
    
    // Text fade-in already triggered during shuffle animation
    // No need to call initSlideAnimations here anymore
    
    setTimeout(() => {
      this.isAnimating = false;
      this.section?.classList.remove('is-carousel-transitioning');
      this.enableButtons();
      
      // Dispatch slideInComplete event
      this.dispatchEvent('slideInComplete', {
        previousIndex,
        currentIndex: newIndex,
        direction,
        currentSlide: newItem
      });
    }, 400);
  }
  
  /**
   * Restore the original forward stack motion: the top card exits left,
   * changes depth out of view, then rejoins the stack from the back.
   */
  animateStackShuffle(newIndex, callback) {
    const topImage = this.images[this.currentIndex];
    if (!topImage) {
      callback();
      return;
    }
    topImage.style.transition = 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)';
    topImage.style.transform = 'translate(-80%, -50%) rotate(-8deg) scale(0.95)';

    setTimeout(() => {
      topImage.style.zIndex = '0';
      const oldIndex = this.currentIndex;
      this.currentIndex = newIndex;

      const rotation = { z: -3, x: -12, y: -16 };
      topImage.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      topImage.style.transform = `translate(-50%, -50%) translateY(${rotation.y}px) rotateX(${rotation.x}deg) rotateZ(${rotation.z}deg)`;
      this.updateImageStack(true);

      const newItem = this.items[newIndex];
      if (newItem) this.fadeInSlideText(newItem);

      this.currentIndex = oldIndex;
      setTimeout(() => {
        topImage.style.transition = '';
        callback();
      }, 500);
    }, 200);
  }

  /**
   * Restore the original reverse stack motion: the bottom card exits left,
   * changes depth out of view, then rejoins as the top card.
   */
  animateStackShuffleReverse(newIndex, callback) {
    const bottomImageIndex = (this.currentIndex + 2) % this.images.length;
    const bottomImage = this.images[bottomImageIndex];
    if (!bottomImage) {
      callback();
      return;
    }
    bottomImage.style.transition = 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)';
    bottomImage.style.transform = 'translate(-80%, -50%) rotate(-8deg) scale(0.95)';

    setTimeout(() => {
      bottomImage.style.zIndex = '10';
      const oldIndex = this.currentIndex;
      this.currentIndex = newIndex;

      const rotation = { z: 0, x: 0, y: 0 };
      bottomImage.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      bottomImage.style.transform = `translate(-50%, -50%) translateY(${rotation.y}px) rotateX(${rotation.x}deg) rotateZ(${rotation.z}deg)`;
      this.updateImageStack(true);

      const newItem = this.items[newIndex];
      if (newItem) this.fadeInSlideText(newItem);

      this.currentIndex = oldIndex;
      setTimeout(() => {
        bottomImage.style.transition = '';
        callback();
      }, 500);
    }, 200);
  }
  
  /**
   * Update counter display
   */
  updateCounter() {
    if (this.currentCounter) {
      this.currentCounter.textContent = this.currentIndex + 1;
    }
  }
  
  /**
   * Disable navigation buttons during animation
   */
  disableButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = true;
    }
    
    if (this.nextBtn) {
      this.nextBtn.disabled = true;
    }
  }
  
  /**
   * Enable navigation buttons after animation
   */
  enableButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = false;
    }
    
    if (this.nextBtn) {
      this.nextBtn.disabled = false;
    }
  }
  
  /**
   * Clean up animations on a slide (kill ScrollTriggers and restore original content)
   */
  cleanupSlideAnimations(slideElement) {
    // Find all animated elements
    const animatedElements = slideElement.querySelectorAll('[data-split-text-reveal], [data-fade-in]');
    
    animatedElements.forEach(element => {
      // Kill any ScrollTriggers associated with this element
      const triggers = ScrollTrigger.getAll().filter(trigger => trigger.trigger === element);
      triggers.forEach(trigger => trigger.kill());
      
      // Kill any active GSAP animations
      gsap.killTweensOf(element);
      
      // Clear all GSAP inline styles
      gsap.set(element, { clearProps: 'all' });
      
      // Restore original HTML content (this is the key!)
      const original = this.originalContent.get(element);
      if (original) {
        element.innerHTML = original.html;
      }
    });
  }
  
  /**
   * Fade in slide text (simpler animation for slide transitions)
   */
  fadeInSlideText(slideElement) {
    const allAnimatedElements = slideElement.querySelectorAll('[data-split-text-reveal], [data-fade-in]');
    
    if (allAnimatedElements.length === 0) return;
    
    // Fade in text (opacity already set to 0 in goToSlide)
    gsap.to(allAnimatedElements, {
      opacity: 1,
      visibility: 'visible',
      duration: 0.5,
      ease: 'power2.out',
      delay: 0
    });
  }
  
  /**
   * For slide transitions, all text fades in together (no split text reveal)
   */
  initSlideAnimations(slideElement) {
    // Find all animated elements
    const allAnimatedElements = slideElement.querySelectorAll('[data-split-text-reveal], [data-fade-in]');
    
    if (allAnimatedElements.length === 0) return;
    
    // Set initial state for all elements
    gsap.set(allAnimatedElements, {
      opacity: 0
    });
    
    // Fade all elements in together - start quickly after slide change
    gsap.to(allAnimatedElements, {
      opacity: 1,
      duration: 0.5,
      ease: 'power2.out',
      delay: 0.1
    });
  }
  
  /**
   * Manually animate split text (bypassing ScrollTrigger)
   */
  animateSplitText(element, delay = 0) {
    // Create SplitTextReveal instance but we'll manually trigger animation
    const instance = new SplitTextReveal(element);
    
    // Get all character masks that were just created
    const charMasks = element.querySelectorAll('[data-char-mask]');
    const charInners = element.querySelectorAll('[data-char-inner]');
    
    if (charInners.length > 0) {
      // Set initial state
      gsap.set(charInners, {
        yPercent: 100,
        opacity: 0
      });
      
      // Animate in with stagger
      gsap.to(charInners, {
        yPercent: 0,
        opacity: 1,
        duration: 1,
        stagger: 0.05,
        ease: 'power2.out',
        delay: delay
      });
    }
  }
  
  /**
   * Manually animate fade in (bypassing ScrollTrigger)
   */
  animateFadeIn(element, delay = 0) {
    const distance = parseFloat(element.dataset.fadeDistance) || 30;
    
    // Set initial state
    gsap.set(element, {
      opacity: 0,
      y: distance
    });
    
    // Animate in
    gsap.to(element, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power2.out',
      delay: delay
    });
  }
  
  /**
   * Dispatch custom event from the carousel element
   */
  dispatchEvent(eventName, detail) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail
    });
    this.element.dispatchEvent(event);
  }
  
  /**
   * Cleanup
   */
  destroy() {
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.previous);
    }
    
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.next);
    }
  }
}
